import { CustomerFormFields } from "@/components/customers/CustomerFormFields";
import { PartyUpsertDialog } from "@/components/parties/PartyUpsertDialog";
import { Button } from "@/components/ui/button";
import {
  createCustomer,
  customerErrorMessage,
  emptyCustomerDraft,
  isCustomerDraftComplete,
  normalizeCustomerDraft,
  validateCustomerDraft,
  type CustomerDraft,
  type CustomerRecord,
} from "@/lib/customers/customersStore";
import { Loader2, User } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (record: CustomerRecord) => void | Promise<void>;
  mode?: "full" | "quick";
  title?: string;
};

export function CreateCustomerDialog({
  open,
  onOpenChange,
  onCreated,
  mode = "full",
  title = "Add customer",
}: Props) {
  const [draft, setDraft] = useState<CustomerDraft>(() => emptyCustomerDraft());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(emptyCustomerDraft());
    setSaving(false);
  }, [open]);

  const save = async () => {
    if (saving) return;
    const validation = validateCustomerDraft(draft);
    if (!validation.valid) {
      toast.error(validation.message ?? "Complete all required customer fields.");
      return;
    }
    const payload = normalizeCustomerDraft(draft);
    setSaving(true);
    try {
      const { record, message } = await createCustomer(payload);
      toast.success(message);
      onOpenChange(false);
      await onCreated?.(record);
    } catch (error) {
      toast.error(customerErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PartyUpsertDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description="Add a customer to your directory with contact and address details."
      icon={User}
      accent="sky"
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void save()}
            disabled={saving || !isCustomerDraftComplete(draft)}
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save customer
          </Button>
        </>
      }
    >
      <CustomerFormFields draft={draft} onChange={setDraft} mode={mode} disabled={saving} />
    </PartyUpsertDialog>
  );
}
